import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import FieldWithInfoBase from '../ui/fieldWithInfo';
import FIELD_INFO from '../../constants/fieldInfo';

const FieldWithInfo = ({ infoKey, children }) => (
  <FieldWithInfoBase info={FIELD_INFO.gameFormat[infoKey]}>{children}</FieldWithInfoBase>
);

const STEPS = [
  'Game Format Details',
  'Ends/Skins/Sets',
  'Points',
  'Tiebreaker',
  'Other',
  'Review'
];

// Extra top padding keeps the floating label of the first field on each
// step from being clipped by the container edge.
const STEP_CONTENT_SX = { display: 'grid', gap: 1.5, paddingTop: 1.5 };

// Bordered sub-section used to group related fields on a step.
const PointsSection = ({ title, children }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.5,
      display: 'grid',
      gap: 1.5
    }}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    {children}
  </Box>
);

const GAME_TYPE_OPTIONS = [
  { value: 2, label: 'Singles' },
  { value: 1, label: 'Pairs' },
  { value: 3, label: 'Triples' },
  { value: 4, label: 'Fours' }
];

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toMultiplier = (value) => Math.max(1, toNumber(value, 1));

// The ends played per skin/set. Derived from Ends Total and the number of
// skins/sets; not directly editable. Ends may be set alongside a Max Score,
// in which case the game ends on whichever is reached first.
const getComputedEnds = (state) => {
  const totalEnds = toNumber(state.totalEnds, 0);
  if (totalEnds > 0) {
    const sets = Math.max(1, toNumber(state.sets, 1));
    return Math.round(totalEnds / sets);
  }

  return toNumber(state.ends, 0);
};

export const buildDefaults = (source = {}, includeName = false) => {
  const safeSource = source || {};

  return {
    id: safeSource.id || '',
    name: includeName ? String(safeSource.name || '') : String(safeSource.name || 'Custom Format'),
    description: String(safeSource.description || ''),
    gameType: toNumber(safeSource.gameType, 1),
    numBowls: toNumber(safeSource.numBowls, 2),
    ends: toNumber(safeSource.ends, 0),
    totalEnds: toNumber(
      safeSource.totalEnds,
      toNumber(safeSource.ends, 0) * toNumber(safeSource.sets, 1)
    ),
    multiplier: toMultiplier(safeSource.multiplier),
    extraEndGamePoint: toNumber(safeSource.extraEndGamePoint, 0),
    sets: toNumber(safeSource.sets, 1),
    maxTotalPerSet: toNumber(safeSource.maxTotalPerSet, 0),
    setAsSkin: safeSource.setAsSkin == null ? true : Boolean(safeSource.setAsSkin),
    excludeSetPointInGamePoints: Boolean(safeSource.excludeSetPointInGamePoints),
    winPoints: toNumber(safeSource.winPoints, 0),
    drawPoints: toNumber(safeSource.drawPoints, 0),
    lossPoints: toNumber(safeSource.lossPoints, 0),
    maxScore: toNumber(safeSource.maxScore, 0),
    playoffs: Boolean(safeSource.playoffs),
    playoffMaxPoints: toNumber(safeSource.playoffMaxPoints, 0),
    playoffPoint: toNumber(safeSource.playoffPoint, 0),
    excludePlayoffPointInGamePoints: Boolean(safeSource.excludePlayoffPointInGamePoints),
    includeHandicap: Boolean(safeSource.includeHandicap),
    rounds: toNumber(safeSource.rounds, 1),
    sections: toNumber(safeSource.sections, 0)
  };
};

const normalizePayload = (values) => {
  return {
    ...values,
    gameType: toNumber(values.gameType, 1),
    numBowls: toNumber(values.numBowls, 2),
    ends: getComputedEnds(values),
    totalEnds: toNumber(values.totalEnds, 0),
    multiplier: toMultiplier(values.multiplier),
    extraEndGamePoint: toNumber(values.extraEndGamePoint, 0),
    sets: toNumber(values.sets, 1),
    maxTotalPerSet: toNumber(values.maxTotalPerSet, 0),
    winPoints: toNumber(values.winPoints, 0),
    drawPoints: toNumber(values.drawPoints, 0),
    lossPoints: toNumber(values.lossPoints, 0),
    maxScore: toNumber(values.maxScore, 0),
    playoffMaxPoints: toNumber(values.playoffMaxPoints, 0),
    playoffPoint: toNumber(values.playoffPoint, 0),
    rounds: toNumber(values.rounds, 1),
    sections: toNumber(values.sections, 0),
    name: String(values.name || '').trim(),
    description: String(values.description || '').trim()
  };
};

const GameFormatWizardDialog = ({
  open,
  onClose,
  initialValues,
  onSave,
  includeName = false,
  title = 'Game Format Wizard',
  saveLabel = 'Save',
  saving = false,
  onDelete,
  embedded = false,
  onBack,
  backButtonLabel = 'Back to Game Format Selection'
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState(buildDefaults(initialValues, includeName));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open || embedded) {
      setActiveStep(0);
      setValues(buildDefaults(initialValues, includeName));
      setError('');
    }
    // Deliberately keyed on open/embedded only. The parent re-creates the
    // initialValues object on each render, and resetting values here would
    // wipe the user's in-progress edits (e.g. the auto-calculated Ends).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedded]);

  const selectedGameTypeLabel = useMemo(() => {
    return (
      GAME_TYPE_OPTIONS.find((option) => option.value === Number(values.gameType))?.label || '-'
    );
  }, [values.gameType]);

  const computedEnds = getComputedEnds(values);

  const handleFieldChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDrawPointsChange = (value) => {
    const isSet = toNumber(value, 0) > 0;
    setValues((prev) => ({
      ...prev,
      drawPoints: value,
      playoffs: isSet ? false : prev.playoffs
    }));
  };

  const validateStep = (step) => {
    if (step === 0) {
      if (includeName && !String(values.name || '').trim()) {
        setError('Game format name is required.');
        return false;
      }

      if (!Number(values.gameType)) {
        setError('Game type is required.');
        return false;
      }
    }

    if (step === 2) {
      if (Number(values.winPoints) < 0 || Number(values.drawPoints) < 0) {
        setError('Points cannot be negative.');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSave = async () => {
    if (!validateStep(0) || !validateStep(2)) {
      return;
    }

    const payload = normalizePayload(values);
    await onSave(payload);
  };

  const content = (
    <>
      {embedded ? (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}>
          <Box>
            {onBack ? (
              <Button variant="outlined" onClick={onBack} disabled={saving}>
                {backButtonLabel}
              </Button>
            ) : null}
          </Box>
          {onDelete ? (
            <Button variant="outlined" color="error" onClick={onDelete} disabled={saving}>
              Delete
            </Button>
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Tabs
          orientation="vertical"
          value={activeStep}
          onChange={(_, value) => setActiveStep(value)}
          variant="scrollable"
          sx={{
            minWidth: { md: 220 },
            borderRight: { md: 2 },
            borderColor: { md: 'divider' },
            '& .MuiTab-root': {
              alignItems: 'flex-start',
              textAlign: 'left',
              minHeight: 44
            }
          }}>
          {STEPS.map((step) => (
            <Tab key={step} label={step} />
          ))}
        </Tabs>

        <Box sx={{ flex: 1 }}>
          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          ) : null}

          {activeStep === 0 && (
            <Box sx={STEP_CONTENT_SX}>
              {includeName ? (
                <FieldWithInfo infoKey="name">
                  <TextField
                    label="Name"
                    value={values.name}
                    onChange={(event) => handleFieldChange('name', event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
              ) : null}
              {includeName ? (
                <FieldWithInfo infoKey="description">
                  <TextField
                    label="Description"
                    value={values.description}
                    onChange={(event) => handleFieldChange('description', event.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </FieldWithInfo>
              ) : null}
              <FieldWithInfo infoKey="gameType">
                <TextField
                  select
                  label="Game Type"
                  value={values.gameType}
                  onChange={(event) => handleFieldChange('gameType', Number(event.target.value))}
                  size="small"
                  fullWidth>
                  {GAME_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldWithInfo>
              <FieldWithInfo infoKey="numBowls">
                <TextField
                  label="Number of Bowls"
                  type="number"
                  value={values.numBowls}
                  onChange={(event) => handleFieldChange('numBowls', event.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldWithInfo>
              <FieldWithInfo infoKey="rounds">
                <TextField
                  label="Rounds"
                  type="number"
                  value={values.rounds}
                  onChange={(event) => handleFieldChange('rounds', event.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldWithInfo>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={STEP_CONTENT_SX}>
              <FieldWithInfo infoKey="setAsSkin">
                <RadioGroup
                  row
                  value={values.setAsSkin ? 'skins' : 'sets'}
                  onChange={(event) => handleFieldChange('setAsSkin', event.target.value === 'skins')}>
                  <FormControlLabel value="skins" control={<Radio />} label="Skins" />
                  <FormControlLabel value="sets" control={<Radio />} label="Sets" />
                </RadioGroup>
              </FieldWithInfo>
              <FieldWithInfo infoKey="totalEnds">
                <TextField
                  label="Number of Ends Total"
                  type="number"
                  value={values.totalEnds}
                  onChange={(event) => handleFieldChange('totalEnds', event.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldWithInfo>
              <FieldWithInfo infoKey="sets">
                <TextField
                  label={values.setAsSkin ? 'Number of Skins' : 'Number of Sets'}
                  type="number"
                  value={values.sets}
                  onChange={(event) => handleFieldChange('sets', event.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldWithInfo>
              <FieldWithInfo infoKey="ends">
                <TextField
                  label={values.setAsSkin ? 'Ends per Skin' : 'Ends per Set'}
                  type="number"
                  value={computedEnds}
                  size="small"
                  fullWidth
                  disabled
                  helperText={
                    toNumber(values.maxScore, 0) > 0
                      ? 'Automatically calculated: Ends Total divided by the number of Skins/Sets. With a Max Score set, the game also ends as soon as either team reaches it.'
                      : 'Automatically calculated: Ends Total divided by the number of Skins/Sets.'
                  }
                />
              </FieldWithInfo>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={STEP_CONTENT_SX}>
              <PointsSection title="End Points">
                <FieldWithInfo infoKey="extraEndGamePoint">
                  <TextField
                    label="Per-End Bonus Point"
                    type="number"
                    value={values.extraEndGamePoint}
                    onChange={(event) => handleFieldChange('extraEndGamePoint', event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
                <FieldWithInfo infoKey="multiplier">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.multiplier > 1}
                        onChange={(event) => {
                          if (event.target.checked) {
                            handleFieldChange(
                              'multiplier',
                              values.multiplier > 1 ? values.multiplier : 2
                            );
                          } else {
                            handleFieldChange('multiplier', 1);
                          }
                        }}
                      />
                    }
                    label="Use Multiplier"
                  />
                </FieldWithInfo>
                {values.multiplier > 1 ? (
                  <TextField
                    label="Multiplier"
                    type="number"
                    value={values.multiplier}
                    onChange={(event) => handleFieldChange('multiplier', event.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 2 }}
                  />
                ) : null}
              </PointsSection>

              <PointsSection title="Set Points">
                <FieldWithInfo infoKey="maxTotalPerSet">
                  <TextField
                    label="Points Per Set"
                    type="number"
                    value={values.maxTotalPerSet}
                    onChange={(event) => handleFieldChange('maxTotalPerSet', event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
                <FieldWithInfo infoKey="excludeSetPointInGamePoints">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.excludeSetPointInGamePoints}
                        onChange={(event) =>
                          handleFieldChange('excludeSetPointInGamePoints', event.target.checked)
                        }
                      />
                    }
                    label="Exclude Set Point from Game Points"
                  />
                </FieldWithInfo>
              </PointsSection>

              <PointsSection title="Max Score">
                <FieldWithInfo infoKey="maxScore">
                  <TextField
                    label="Max Score"
                    type="number"
                    value={values.maxScore}
                    onChange={(event) => handleFieldChange('maxScore', event.target.value)}
                    size="small"
                    fullWidth
                    helperText={
                      computedEnds > 0
                        ? 'The game ends as soon as either team reaches this score, or when the Ends are up - whichever happens first. Leave as 0 to play purely to the Ends.'
                        : 'The score a team must reach to win the game. Leave as 0 to play to a fixed number of Ends instead.'
                    }
                  />
                </FieldWithInfo>
              </PointsSection>

              <PointsSection title="Game Points">
                <FieldWithInfo infoKey="winPoints">
                  <TextField
                    label="Win Points"
                    type="number"
                    value={values.winPoints}
                    onChange={(event) => handleFieldChange('winPoints', event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
                <FieldWithInfo infoKey="drawPoints">
                  <TextField
                    label="Draw Points"
                    type="number"
                    value={values.drawPoints}
                    onChange={(event) => handleDrawPointsChange(event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
                <FieldWithInfo infoKey="lossPoints">
                  <TextField
                    label="Loss Points"
                    type="number"
                    value={values.lossPoints}
                    onChange={(event) => handleFieldChange('lossPoints', event.target.value)}
                    size="small"
                    fullWidth
                  />
                </FieldWithInfo>
              </PointsSection>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={STEP_CONTENT_SX}>
              <FieldWithInfo infoKey="playoffs">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.playoffs}
                      onChange={(event) => handleFieldChange('playoffs', event.target.checked)}
                      disabled={toNumber(values.drawPoints, 0) > 0}
                    />
                  }
                  label="Use Tiebreaker"
                />
              </FieldWithInfo>
              {toNumber(values.drawPoints, 0) > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Note: Tiebreaker cannot be used if Draw Points is greater than 0.
                </Typography>
              ) : null}
              {values.playoffs ? (
                <>
                  <FieldWithInfo infoKey="playoffMaxPoints">
                    <TextField
                      label="Tiebreaker Max Points"
                      type="number"
                      value={values.playoffMaxPoints}
                      onChange={(event) =>
                        handleFieldChange('playoffMaxPoints', event.target.value)
                      }
                      size="small"
                      fullWidth
                    />
                  </FieldWithInfo>
                  <FieldWithInfo infoKey="playoffPoint">
                    <TextField
                      label="Tiebreaker Point"
                      type="number"
                      value={values.playoffPoint}
                      onChange={(event) => handleFieldChange('playoffPoint', event.target.value)}
                      size="small"
                      fullWidth
                    />
                  </FieldWithInfo>
                  <FieldWithInfo infoKey="excludePlayoffPointInGamePoints">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.excludePlayoffPointInGamePoints}
                          onChange={(event) =>
                            handleFieldChange(
                              'excludePlayoffPointInGamePoints',
                              event.target.checked
                            )
                          }
                        />
                      }
                      label="Exclude Tiebreaker Point from Game Points"
                    />
                  </FieldWithInfo>
                </>
              ) : null}
            </Box>
          )}

          {activeStep === 4 && (
            <Box sx={STEP_CONTENT_SX}>
              <FieldWithInfo infoKey="includeHandicap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.includeHandicap}
                      onChange={(event) =>
                        handleFieldChange('includeHandicap', event.target.checked)
                      }
                    />
                  }
                  label="Include Handicap"
                />
              </FieldWithInfo>
              <FieldWithInfo infoKey="sections">
                <TextField
                  label="Sections"
                  type="number"
                  value={values.sections}
                  onChange={(event) => handleFieldChange('sections', event.target.value)}
                  size="small"
                  fullWidth
                />
              </FieldWithInfo>
            </Box>
          )}

          {activeStep === 5 && (
            <Box sx={{ display: 'grid', gap: 1, paddingTop: 1.5 }}>
              {includeName ? (
                <Typography variant="body2">Name: {values.name || '-'}</Typography>
              ) : null}
              <Typography variant="body2">Game Type: {selectedGameTypeLabel}</Typography>
              <Typography variant="body2">
                Mode: {values.setAsSkin ? 'Skins' : 'Sets'} ({values.sets})
              </Typography>
              <Typography variant="body2">Ends Total: {values.totalEnds}</Typography>
              <Typography variant="body2">
                Ends per {values.setAsSkin ? 'Skin' : 'Set'} (auto): {computedEnds}
              </Typography>
              <Typography variant="body2">
                Max Score: {toNumber(values.maxScore, 0) > 0 ? values.maxScore : 'None'}
              </Typography>
              <Typography variant="body2">
                Win/Draw/Loss: {values.winPoints}/{values.drawPoints}/{values.lossPoints}
              </Typography>
              <Typography variant="body2">Tiebreaker: {values.playoffs ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">
                Handicap: {values.includeHandicap ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.default',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'
        }}>
        {content}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onBack || onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleBack} disabled={saving || activeStep === 0}>
            Back
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={saving} variant="contained">
              Next
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving} variant="contained">
              {saveLabel}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        {onDelete && activeStep === STEPS.length - 1 ? (
          <Button onClick={onDelete} color="error" disabled={saving}>
            Delete
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleBack} disabled={saving || activeStep === 0}>
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={saving} variant="contained">
            Next
          </Button>
        ) : (
          <Button onClick={handleSave} disabled={saving} variant="contained">
            {saveLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GameFormatWizardDialog;
